<template>
  <app-shell breadcrumbs="Home, Visits">
    <div class="visits__container">
      <div class="row no-gutters mt-4">
        <div class="col-12">
          <!-- Visits Chart -->
          <div class="visits__chart-container">
            <comparison-board :data="comparisonData" @filter-chart="rebuildComparisonChart"></comparison-board>
          </div>
          <!-- End of the visits chart -->
        </div>
      </div>

      <div class="row no-gutters mt-4">
        <div class="col-12">
          <!-- Visits data table -->
          <div class="visits__data-table-container">
            <data-table :actions="actions" :buttons="buttons" :column-configuration="columns" :filters="filters"
                        :selected-row="selectedRow" :page="page" :page-limit="pageLimit" :rows="rows"
                        @filter="rebuildTable" @next-page="nextPage" @new-visit="launchNewVisit" @prev-page="prevPage"
                        @show-payments="showPayments" @simulate-payment="simulateVisitPayment" @view="selectRow">
              <div class="ml-data-table__placeholder">
                <i class="fas fa-chart-bar fa-3x"></i>
                <h5 class="my-3">You do not have any visits yet</h5>
              </div>
            </data-table>
          </div>
          <!-- End of the visits data table -->
        </div>
      </div>

      <!-- Simulate a payment -->
      <modal action="Pay" :name="`simulate-payment`" :no-icon="false" :title="`<b>Simulate</b> a payment`"
             @cancel="cancelSimulator" @save="simulatePayment">
        <div class="form-group">
          <label class="control-label" for="phone_number">Mobile number</label>
          <input type="text"
                 class="form-control"
                 name="phone_number"
                 id="phone_number"
                 v-model="simulatedPayment.MSISDN"
                 v-validate="'required|phone_number'"
          >
          <span v-show="errors.has('phone_number')" class="help is-danger">
            {{ errors.first('phone_number') }}
          </span>
        </div>

        <div class="form-group">
          <label class="control-label" for="amount">Amount</label>
          <input type="number"
                 class="form-control"
                 name="amount"
                 id="amount"
                 v-model="simulatedPayment.chargeAmount"
                 v-validate="'required|min_value:1'"
          >
          <span v-show="errors.has('amount')" class="help is-danger">
            {{ errors.first('amount') }}
          </span>
        </div>

        <div class="form-group">
          <label class="control-label" for="payment_option">Payment option</label>
          <select id="payment_option" name="payment_option" class="form-control"
                  v-model="simulatedPayment.paymentOption" v-validate="'required'">
            <option v-for="(client, key) in _preparePayerClients(meta.payerClients)" :value="client" :key="key"> {{ client.label }} </option>
          </select>
          <span v-show="errors.has('payment_option')" class="help is-danger">
            {{ errors.first('payment_option') }}
          </span>
        </div>
      </modal>
      <!-- End of simulate payment -->

      <!-- Simulate a visit -->
      <modal action="Launch" :name="`launch-new-visit`" :no-icon="false" :title="`<b>Launch</b> a new visit`"
             @cancel="cancelVisitSimulator" @save="redirectToCheckout">
        <div class="form-group">
          <label class="control-label" for="visit_phone_number">Mobile number</label>
          <input type="text"
                 class="form-control"
                 name="visit_phone_number"
                 id="visit_phone_number"
                 v-model="newVisit.MSISDN"
                 v-validate="'required|phone_number'"
          >
          <span v-show="errors.has('visit_phone_number')" class="help is-danger">
            {{ errors.first('visit_phone_number') }}
          </span>
        </div>

        <div class="form-group">
          <label class="control-label" for="visit_amount">Amount</label>
          <input type="number"
                 class="form-control"
                 name="visit_amount"
                 id="visit_amount"
                 v-model="newVisit.amount"
                 v-validate="'required|min_value:1'"
          >
          <span v-show="errors.has('visit_amount')" class="help is-danger">
            {{ errors.first('visit_amount') }}
          </span>
        </div>

        <div class="form-group">
          <label class="control-label" for="visit_country_code">Country</label>
          <select id="visit_country_code" name="visit_country_code" class="form-control"
                  v-model="newVisit.countryCode" v-validate="'required'">
            <option v-for="(country, key) in countries" :value="country.code" :key="key"> {{ country.name }} </option>
          </select>
          <span v-show="errors.has('visit_country_code')" class="help is-danger">
            {{ errors.first('visit_country_code') }}
          </span>
        </div>

        <div class="form-group">
          <label class="control-label" for="visit_currency_code">Currency</label>
          <select id="visit_currency_code" name="visit_currency_code" class="form-control"
                  v-model="newVisit.currencyCode" v-validate="'required'">
            <option v-for="(currency, key) in currencies" :value="currency.code" :key="key"> {{ currency.name }} </option>
          </select>
          <span v-show="errors.has('visit_currency_code')" class="help is-danger">
            {{ errors.first('visit_currency_code') }}
          </span>
        </div>
      </modal>
      <!-- End of simulate visit -->
    </div>
  </app-shell>
</template>

<script>
  import _ from 'lodash'
  import { mapGetters } from 'vuex'
  import Config from '@/api/config'
  import Modal from '@/components/Modal'
  import Shell from '@/components/Shell'
  import RandomString from 'randomstring'
  import Visits from '@/api/services/visits'
  import Express from '@/api/utilities/Express'
  import DataTable from '@/components/DataTable'
  import DataTableMixin from '@/mixins/DataTable'
  import ComparisonDataMixin from '@/mixins/ComparisonData'
  import ComparisonBoard from '@/components/ComparisonBoard'
  import PaymentSimulatorMixin from '@/mixins/PaymentSimulator'

  const logInfo = !!(process.env.NODE_ENV !== 'production')

  export default {
    components: {
      'modal': Modal,
      'app-shell': Shell,
      'data-table': DataTable,
      'comparison-board': ComparisonBoard
    },
    computed: {
      ...mapGetters('services', ['currentService']),
      ...mapGetters('user', ['expressKeys', 'userInfo']),
      countries () {
        return Config.COUNTRIES
      },
      currencies () {
        return Config.CURRENCIES
      },
      filters () {
        return [
          {
            label: 'Account number',
            type: 'string',
            key: 'accountNumber'
          },
          {
            label: 'Amount',
            type: 'number',
            key: 'requestAmount'
          },
          {
            label: 'Visit ID',
            type: 'string',
            key: 'checkoutRequestID',
            ...this._passedFilters('checkoutRequestID')
          },
          {
            label: 'Country',
            type: 'list',
            key: 'countryCode',
            list: this._prepareCountries(this.meta.countries)
          },
          {
            label: 'Currency',
            type: 'list',
            key: 'originalCurrencyCode',
            list: this._prepareCurrencies(this.meta.currencies)
          },
          {
            label: 'Date',
            type: 'date',
            key: 'date'
          },
          {
            label: 'Mobile number',
            type: 'string',
            key: 'MSISDN'
          },
          {
            label: 'Status',
            type: 'list',
            key: 'overallStatus',
            list: this._prepareStatuses(this.meta.statusCodes)
          }
        ]
      }
    },
    data () {
      return {
        actions: [
          {
            name: 'View',
            event: 'view'
          },
          {
            name: 'Pay',
            event: 'simulate-payment'
          },
          {
            name: 'Payments',
            event: 'show-payments'
          }
        ],
        buttons: [{
          name: 'Simulate',
          icon: 'open_in_new',
          event: 'new-visit'
        }],
        columns: {
          visitID: {
            label: 'Visit ID'
          },
          amount: {
            float: 'left',
            filter: 'money',
            label: 'Amount',
            highlight: true
          },
          currency: {
            float: 'right',
            highlight: true,
            label: ''
          },
          mobile_number: {
            filter: 'phoneNumber',
            label: 'Mobile Number'
          },
          status: {
            filter: 'capitalize'
          },
          date: {
            float: 'right',
            label: 'Date',
            type: 'date'
          },
          account_number: {
            label: 'Account Number',
            collapsed: true
          },
          conversion_rate: {
            label: 'Conversion rate',
            collapsed: true
          },
          converted_amount: {
            collapsed: true
          },
          amount_paid: {
            label: 'Amount Paid',
            collapsed: true
          },
          customer_email: {
            label: 'Customer Email',
            collapsed: true
          },
          merchant_transaction_id: {
            collapsed: true
          },
          original_amount: {
            collapsed: true
          }
        },
        newVisit: {
          accountNumber: '',
          merchantTransactionID: '',
          MSISDN: '',
          countryCode: '',
          currencyCode: '',
          amount: 100
        }
      }
    },
    methods: {
      _formatVisits (visits) {
        let structuredVisits = []
        _.forIn(visits, visit => {
          structuredVisits.push({
            visitID: visit.checkoutRequestID,
            currency: visit.originalCurrencyCode,
            amount: visit.requestAmount,
            mobile_number: visit.MSISDN,
            status: visit.statusCodeDesc,
            date: visit.dateCreated,
            account_number: visit.accountNumber,
            customer_email: visit.customerEmail,
            merchant_transaction_id: visit.merchantTransactionID,
            conversion_rate: visit.conversionRate,
            converted_amount: visit.convertedCurrencyCode + ' ' + visit.convertedAmount,
            amount_paid: visit.convertedCurrencyCode + ' ' + ((visit.amountPaid === null) ? 0 : visit.amountPaid)
          })
        })
        return structuredVisits
      },
      _passedFilters () {
        logInfo && console.log('What was the filter passed ?', this.$route.query.filter)
        if (this.$route.query.filter !== undefined) {
          return {
            mode: 'like',
            selected: true,
            value: this.$route.query.filter
          }
        }
        return {}
      },
      cancelVisitSimulator () {
        this.$modal.hide('launch-new-visit')
        // Reset the user form
        this.newVisit = {
          accountNumber: '',
          merchantTransactionID: '',
          MSISDN: '',
          countryCode: '',
          currencyCode: '',
          amount: 100
        }
      },
      launchNewVisit () {
        // Prefill the mobile number with the user's number
        this.newVisit.MSISDN = this.userInfo.MSISDN
        this.newVisit.countryCode = this.userInfo.businessCountryCode
        this.newVisit.currencyCode = Config.CURRENCIES[this.userInfo.businessCountryCode].code
        // Generate the hidden fields
        this.newVisit.accountNumber = RandomString.generate({length: 4, charset: 'alphabetic'}) + ((new Date()).getTime() / 1000)
        this.$modal.show('launch-new-visit')
      },
      showPayments (visit) {
        logInfo && console.log('Select payments for the visit: ', visit)
        this.$router.push('/payments?filter=' + visit.visitID)
      },
      rebuildTable (filters, page = null, rebuildFilters = false) {
        let formattedFilters = this._extractFormattedFilters(filters)
        logInfo && console.log('Filters from the data table', formattedFilters)
        Visits.whereService({ serviceCode: this.currentService.serviceCode }).fetchAll({ page: (page === null) ? this.page : page, filters: formattedFilters })
          .then(response => {
            logInfo && console.log('Visits fetched from the engine => ', response)
            this.rows = this._formatVisits(response.DATA.checkoutRequests)
            this.page = response.DATA.page
            this.pageLimit = response.DATA.pageLimit
            if (rebuildFilters) {
              this.meta = response.DATA.metaData
              console.log(this.meta, "testing response")
            }
          })
          .catch(reason => {
            logInfo && console.log('Fetching visits from the engine failed => ', reason)
          })
      },
      rebuildComparisonChart (filters) {
        logInfo && console.log('Rebuilding the comparison chart....', filters)
        Visits.whereService({ serviceCode: this.currentService.serviceCode }).fetchComparisonData({ filters })
          .then(response => {
            logInfo && console.log('Comparison chart data received => ', response)
            this.comparisonData = response
          })
          .catch(reason => {
            logInfo && console.log('Failed to rebuild the comparison chart => ', reason)
          })
      },
      redirectToCheckout () {
        this.$snackbar.open({
          indefinite: true,
          message: 'Redirecting to checkout in a few...',
          type: 'is-success',
          position: 'is-bottom',
          queue: false
        })
        Express.initialize({
          requestAmount: this.newVisit.amount,
          countryCode: this.newVisit.countryCode,
          currencyCode: this.newVisit.currencyCode,
          customerEmail: this.userInfo.emailAddress,
          merchantTransactionID: RandomString.generate({length: 18, charset: 'alphabetic'}),
          MSISDN: this.newVisit.MSISDN,
          accountNumber: this.newVisit.accountNumber,
          serviceCode: this.currentService.serviceCode
        }).redirect({
          accessKey: this.expressKeys.access_key,
          ivKey: this.expressKeys.iv_key,
          secretKey: this.expressKeys.secret_key
        })
      },
      simulateVisitPayment (event) {
        this.simulatedPayment.accountNumber = event.account_number
        this.launchSimulator()
      }
    },
    mixins: [ComparisonDataMixin, DataTableMixin, PaymentSimulatorMixin],
    mounted () {
      this.rebuildTable(this.filters, 0, true)
      this.attachCustomValidators()
    },
    name: 'Visits'
  }
</script>



// WEBPACK FOOTER //
// src/views/Visits.vue